import { createFileRoute } from '@tanstack/react-router';
import styled from 'styled-components';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import StudentStudyActivitiesTable from '@/tables/StudentStudyActivitiesTable';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/course-study/study-activities',
)({
  component: StudyActivitiesScreen,
});

function StudyActivitiesScreen() {
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);

  // Legacy refuses this tab by never registering it, so a denied viewer gets a
  // 404 page rather than a permission message. The probe answers 200 for every
  // authenticated caller — it is a capability question, not an existence oracle
  // over students — so one query decides the page without reading a status code,
  // and it carries the caption the table needs without widening `getStudent`
  // (17 fields, and admin-or-self, so a study helper cannot call it at all).
  //
  // Defence in depth over a NARROWER set than the shell's, not a duplicate of it.
  // The shell gates on `can_view` (`$memberId.course-study.tsx:29`), which is
  // COURSE_STUDY_VIEWERS = RELATED_VIEWERS — SELF included — and it only drops
  // this tab from the strip when `is_supervisor` is false. A student typing the
  // URL clears the shell and lands here, so the refusal still has to live here:
  // the set this leaf asks for is SUPERVISOR_VIEWERS = RELATED_VIEWERS - {SELF}
  // (`study_activities/permissions.py:82`).
  const { data: access, isLoading } =
    api.getStudentStudyActivityAccess.useQuery({
      path: { student_id: studentId },
    });

  if (isLoading) return null;

  // Positive: `access` is undefined on a 401, a 500 or a dropped connection, and
  // every one of those is a refusal. A `can_view === false` test falls through
  // on all three and renders the page.
  if (access?.can_view !== true) return <NotFoundScreen />;

  return (
    <Container>
      <StudentStudyActivitiesTable
        studentId={studentId}
        studentName={access.display_name ?? ''}
      />
    </Container>
  );
}
const Container = styled.div`
  padding-top: 8px;
`;
